import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { Clickable } from "../components/Clickables";
import { H, HLevel } from "../components/Headings";
import { PageSkeleton } from "../components/PageSkeleton";
import { TrackedStreamField } from "../components/PageSkeleton/TrackedStreamField";
import { RichTextBlock } from "../components/RichText";

import styles from "./definition.module.scss";

export const Template = (props: templates.DefinitionPageTemplate) => {
    const {
        title,
        search_description,
        short_definition,
        body,
        also_known_as,
        related_term_links,
        sources,
        auto_schema_json,
    } = props.page;
    // Skip sources with no resolvable URL (e.g. a deleted page) so they don't
    // render as an empty-href link back to this page.
    const linkedSources = sources.value.filter((block) => block.value.url);

    return (
        <PageSkeleton
            {...props.page}
            schema_json={auto_schema_json}
            // Fall back to the short definition when no meta description is set.
            search_description={search_description || short_definition}
            isPreview={props.is_preview}
            // The term is rendered below as the page's visible <h1>.
            hasVisibleH1
        >
            <article className={styles.root}>
                <HLevel level={1}>
                    <H className={styles.term}>{title}</H>
                    <HLevel>
                        <section className={styles.intro}>
                            <H className={styles.question}>
                                {t`What is ${title}?`}
                            </H>
                            <p className={styles.shortDef}>
                                {short_definition}
                            </p>
                        </section>
                        <div className={styles.body}>
                            <TrackedStreamField
                                tagName={React.Fragment}
                                value={body.value}
                                components={{ rich_text: RichTextBlock }}
                            />
                        </div>
                        {also_known_as && (
                            <section className={styles.aside}>
                                <H className={styles.asideHeading}>
                                    {t`Also known as`}
                                </H>
                                <p>{also_known_as}</p>
                            </section>
                        )}
                        {related_term_links.length > 0 && (
                            <section className={styles.aside}>
                                <H className={styles.asideHeading}>
                                    {t`Related terms`}
                                </H>
                                <ul className={styles.list}>
                                    {related_term_links.map((link) => (
                                        <li key={link.url}>
                                            <Clickable href={link.url}>
                                                {link.title}
                                            </Clickable>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}
                        {linkedSources.length > 0 && (
                            <section className={styles.aside}>
                                <H className={styles.asideHeading}>
                                    {t`Sources & further reading`}
                                </H>
                                <ul className={styles.list}>
                                    {linkedSources.map((block) => (
                                        <li key={block.id}>
                                            {/* Fall back to the URL so an
                                                external source with a blank
                                                display name still has
                                                visible, accessible text. */}
                                            <Clickable simpleLink={block.value}>
                                                {block.value.display_name ||
                                                    block.value.url}
                                            </Clickable>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </HLevel>
                </HLevel>
            </article>
        </PageSkeleton>
    );
};
